import { DateRange, CommentType, SortBy } from 'models/CommentHistory';
import { Severity } from 'Common/components/Molecules/CommentSeverityIcon';

export interface State {
  organizationUUID?: string;
  dateRange: DateRange;
  commentType: CommentType;
  severity?: Severity;
  category?: string;
  author?: string;
  repositoryUUID?: string;
  sortBy: SortBy;
  page: number;
}

export const initialState: State = {
  dateRange: 'last_30',
  commentType: 'inline',
  page: 1,
  sortBy: 'severity',
};

export enum Type {
  SET_ORGANIZATION_UUID = 'COMMENTS/CONTROLS/SET_ORGANIZATION_UUID',
  SET_DATE_RANGE = 'COMMENTS/CONTROLS/SET_DATE_RANGE',
  SET_COMMENT_TYPE = 'COMMENTS/CONTROLS/SET_COMMENT_TYPE',
  SET_SEVERITY = 'COMMENTS/CONTROLS/SET_SEVERITY',
  SET_CATEGORY = 'COMMENTS/CONTROLS/SET_CATEGORY',
  SET_AUTHOR = 'COMMENTS/CONTROLS/SET_AUTHOR',
  SET_REPO_UUID = 'COMMENTS/CONTROLS/SET_REPO_UUID',
  RESET_FILTERS = 'COMMENTS/CONTROLS/RESET_FILTERS',
  SET_SORT_BY = 'COMMENTS/CONTROLS/SET_SORT_BY',
  SET_PAGE = 'COMMENTS/CONTROLS/SET_PAGE',
}

interface Action<T> {
  type: T;
}

export interface SetOrganizationUUID
  extends Action<Type.SET_ORGANIZATION_UUID> {
  organizationUUID?: string;
}

export interface SetDateRange extends Action<Type.SET_DATE_RANGE> {
  dateRange: DateRange;
}

export interface SetCommentType extends Action<Type.SET_COMMENT_TYPE> {
  commentType: CommentType;
}

export interface SetSeverity extends Action<Type.SET_SEVERITY> {
  severity?: Severity;
}

export interface SetCategory extends Action<Type.SET_CATEGORY> {
  category?: string;
}

export interface SetAuthor extends Action<Type.SET_AUTHOR> {
  author?: string;
}

export interface SetRepoUUID extends Action<Type.SET_REPO_UUID> {
  repositoryUUID?: string;
}

export interface ResetFilters extends Action<Type.RESET_FILTERS> {}

export interface SetSortBy extends Action<Type.SET_SORT_BY> {
  sortBy: SortBy;
}

export interface SetPage extends Action<Type.SET_PAGE> {
  page: number;
}

export type Actions =
  | SetOrganizationUUID
  | SetDateRange
  | SetCommentType
  | SetSeverity
  | SetCategory
  | SetAuthor
  | SetRepoUUID
  | ResetFilters
  | SetSortBy
  | SetPage;

export default (state = initialState, action: Actions): State => {
  switch (action.type) {
    case Type.SET_ORGANIZATION_UUID:
      return { ...state, organizationUUID: action.organizationUUID, page: 1 };
    case Type.SET_DATE_RANGE:
      return {
        ...state,
        dateRange: action.dateRange,
        page: 1,
      };
    case Type.SET_COMMENT_TYPE:
      const commentType = action.commentType;

      return {
        ...state,
        commentType,
        sortBy: commentType === 'inline' ? 'severity' : 'created_at',
        page: 1,
      };
    case Type.SET_SEVERITY:
      return {
        ...state,
        severity: action.severity,
        page: 1,
      };
    case Type.SET_CATEGORY:
      return {
        ...state,
        category: action.category,
        page: 1,
      };
    case Type.SET_AUTHOR:
      return {
        ...state,
        author: action.author,
        page: 1,
      };
    case Type.SET_REPO_UUID:
      return {
        ...state,
        repositoryUUID: action.repositoryUUID,
        page: 1,
      };
    case Type.RESET_FILTERS:
      return {
        ...state,
        commentType: 'inline',
        severity: undefined,
        category: undefined,
        author: undefined,
        repositoryUUID: undefined,
        sortBy: 'severity',
        page: 1,
      };
    case Type.SET_SORT_BY:
      const sortBy =
        state.commentType === 'inline' ? action.sortBy : 'created_at';

      return {
        ...state,
        sortBy,
        page: 1,
      };
    case Type.SET_PAGE:
      return { ...state, page: action.page };
    default:
      return state;
  }
};

export const actions = {
  loadCommentHistory: (organizationUUID: string): SetOrganizationUUID => ({
    type: Type.SET_ORGANIZATION_UUID,
    organizationUUID,
  }),
  setDateRange: (dateRange: DateRange): SetDateRange => ({
    type: Type.SET_DATE_RANGE,
    dateRange,
  }),
  setCommentType: (commentType: CommentType): SetCommentType => ({
    type: Type.SET_COMMENT_TYPE,
    commentType,
  }),
  setSeverity: (severity: Severity): SetSeverity => ({
    type: Type.SET_SEVERITY,
    severity,
  }),
  setCategory: (category: string): SetCategory => ({
    type: Type.SET_CATEGORY,
    category,
  }),
  setAuthor: (author: string): SetAuthor => ({
    type: Type.SET_AUTHOR,
    author,
  }),
  setRepositoryUUID: (repositoryUUID: string): SetRepoUUID => ({
    type: Type.SET_REPO_UUID,
    repositoryUUID,
  }),
  resetFilters: (): ResetFilters => ({ type: Type.RESET_FILTERS }),
  setSortBy: (sortBy: SortBy): SetSortBy => ({
    type: Type.SET_SORT_BY,
    sortBy,
  }),
  setPage: (page: number): SetPage => ({
    type: Type.SET_PAGE,
    page,
  }),
};
